/**
 * Blast-radius confirm (spec docs/superpowers/specs/2026-09-16-blast-radius-disclosure-design.md).
 * Wraps ConfirmDialog: counted lines from POST /api/deletion-impact, words from
 * @overlayqa/shared describeDeletionImpact. Any failure renders `fallback`,
 * which callers pass as today's copy verbatim.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { deletionConfirmLabel, describeDeletionImpact, typedPhraseMatches, DELETION_IMPACT_COUNTING_TEXT, type DeletionImpactRequest } from '@overlayqa/shared';
import { ConfirmDialog } from './ConfirmDialog';
import { useDeletionImpact } from '@/hooks/useDeletionImpact';

interface DeletionConfirmDialogProps {
  open: boolean;
  request: DeletionImpactRequest | null;
  fallback: { title: string; message: string; confirmLabel: string; typedPhrase?: string };
  /**
   * Called with the phrase the user typed (empty string when the confirm asks
   * for none), so a caller can keep its own gate on it rather than trusting
   * this dialog's `confirmDisabled`. Existing `() => void` handlers are
   * unaffected.
   */
  onConfirm: (typed: string) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  children?: ReactNode;
  confirmDisabled?: boolean;
  maxWidthClass?: string;
  /** A recovery read can replace the destructive action after an uncertain write. */
  confirmLabelOverride?: string;
}

export function DeletionConfirmDialog({ open, request, fallback, onConfirm, onCancel, loading = false, error = null, children, confirmDisabled = false, maxWidthClass, confirmLabelOverride }: DeletionConfirmDialogProps) {
  const query = useDeletionImpact(request, open);
  const [typed, setTyped] = useState('');
  useEffect(() => { if (!open) setTyped(''); }, [open]);

  const copy = query.data ? describeDeletionImpact(query.data) : null;
  const counting = open && query.isPending && request !== null;
  const typedPhrase = copy ? copy.typedPhrase : (fallback.typedPhrase ?? null);
  const phraseOk = typedPhrase === null || typedPhraseMatches(typed, typedPhrase);
  // R29: one label from the caller's own count in EVERY state (counting,
  // counted, failed), as the extension and team review already do. Reading
  // `copy?.confirmLabel ?? fallback.confirmLabel` made it "Delete" while
  // counting and "Delete 2 issues" once counted; the footer is right-aligned,
  // so the button grew leftward onto ground Cancel had just held.
  const confirmLabel = request ? deletionConfirmLabel(request.kind, request.ids.length) : fallback.confirmLabel;

  return (
    <ConfirmDialog
      open={open}
      title={copy?.title ?? fallback.title}
      message={copy || counting ? undefined : fallback.message}
      confirmLabel={confirmLabelOverride ?? confirmLabel}
      onConfirm={() => onConfirm(typed)}
      onCancel={onCancel}
      destructive
      loading={loading}
      confirmDisabled={!phraseOk || confirmDisabled}
      maxWidthClass={maxWidthClass}
      error={error}
    >
      {/*
        R17: COUNTING renders today's message FIRST and the progress note
        beneath it, so a confirm never discloses LESS than it did before the
        count existed while its danger button is live. Rendering the note
        through `message` alone dropped both the count and "This cannot be
        undone." for up to the hook's 3000ms ceiling. Both lines live here
        rather than one in `message` and one here, so their order is
        structural. Matches the extension and team-review notices, which reach
        the same pairing through their own DeletionImpactNotice.
      */}
      {counting ? (
        <div className="mb-5 text-sm" data-testid="deletion-impact-counting">
          <p className="text-gray-600">{fallback.message}</p>
          <p className="mt-1 text-gray-500">{DELETION_IMPACT_COUNTING_TEXT}</p>
        </div>
      ) : null}
      {copy ? (
        <div className="mb-5 text-sm text-gray-600" data-testid="deletion-impact">
          {copy.lead ? <p className="mb-2">{copy.lead}</p> : null}
          {copy.lines.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1">
              {copy.lines.map((line) => <li key={line}>{line}</li>)}
            </ul>
          ) : null}
          {copy.keepLines.map((line) => <p key={line} className="mt-3 text-gray-500">{line}</p>)}
        </div>
      ) : null}
      {children}
      {typedPhrase !== null ? (
        <div className="mb-5">
          <label htmlFor="deletion-typed-confirm" className="section-title">Type "{typedPhrase}" to confirm</label>
          <input
            id="deletion-typed-confirm"
            className="input w-full mt-1"
            value={typed}
            disabled={loading}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            placeholder={typedPhrase}
          />
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
